﻿import { ErrorModal } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";
import type { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import type { SectionsResponse } from "../repositories/SectionRepository";
import { getCurrentSection, useSections } from "./SectionsOfPartSelector";

const SECTION_SORT = {
	UPCOMING: "upcoming",
	RECENT: "recent",
	ALPHABETICAL: "alphabetical",
} as const;

type SectionSort = (typeof SECTION_SORT)[keyof typeof SECTION_SORT];

const sortSections = (
	sectionResponse: SectionsResponse,
	sortBy: SectionSort,
) => {
	const sections = sectionResponse.sections;

	if (sortBy === SECTION_SORT.ALPHABETICAL) {
		return sections.toSorted((a, b) =>
			a.SectionName.localeCompare(b.SectionName),
		);
	}

	const events =
		sortBy === SECTION_SORT.RECENT
			? sectionResponse.recentEvents
			: sectionResponse.upcomingEvents;

	return sections.toSorted((a, b) => {
		const aEvent = events?.find((event) => event.SectionId === a.SectionId);
		const bEvent = events?.find((event) => event.SectionId === b.SectionId);
		const aIndex = aEvent?.EventIndex ?? Number.MAX_VALUE;
		const bIndex = bEvent?.EventIndex ?? Number.MAX_VALUE;
		if (aIndex !== bIndex) return aIndex - bIndex;
		return a.SectionName.localeCompare(b.SectionName);
	});
};

interface Props {
	label?: string;
	value: number | null;
	readOnly?: boolean;
	onChange?: (
		value: number | null,
		name?: string | null,
		tph?: number | null,
		baseTargetTPH?: number | null,
		targetTPHPercentage?: number | null,
		millYield?: number | null,
		finishedYield?: number | null,
	) => void;
	disabled?: boolean;
	required?: boolean;
	fieldError?: FieldError;
	includeMostUsed?: boolean;
	onCurrentClick?: () => void;
	size?: "small" | "medium";
	/** Omit to keep themeMESF's dense margin. */
	margin?: "none" | "dense" | "normal";
	sortBy?: SectionSort;
	hideTph?: boolean;
	assetId?: number;
	shiftId?: number;
}

export const SectionSelector = ({
	label = "Section",
	value,
	readOnly = false,
	onChange = () => {},
	disabled = false,
	required = false,
	fieldError,
	includeMostUsed = false,
	onCurrentClick,
	size = "medium",
	margin,
	sortBy = SECTION_SORT.UPCOMING,
	hideTph = false,
	assetId,
	shiftId,
}: Props) => {
	const [error, setError] = useState("");

	const { data, isLoading } = useSections({
		refetchInterval: 60_000,
		assetId,
		shiftId,
		onError: (error) => setError(error.message),
	});

	const sections = data?.sections;
	const upcomingEvents = data?.upcomingEvents;
	const options = data
		? shiftId !== undefined
			? data.sections
			: sortSections(data, sortBy)
		: [];

	const selectedValue =
		sections?.find((section) => section.SectionId === value) || null;
	const currentSection = getCurrentSection(data);

	const autocompleteComponent = (
		<Autocomplete
			id="clear-on-escape"
			size="small"
			clearOnEscape
			autoHighlight
			options={options}
			getOptionLabel={(option) => option.SectionName}
			disabled={disabled}
			renderOption={(props, option) => (
				<Box
					{...props}
					component="li"
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-start",
						width: "100%",
					}}
				>
					<Box
						sx={{
							width: "100%",
						}}
					>
						{option.SectionName}
					</Box>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: "4px",
							width: "100%",
						}}
					>
						{option.SectionFamilyName && (
							<Box sx={{ fontSize: "0.8em", color: "grey" }}>
								{`Family: ${option.SectionFamilyName}${
									!hideTph && option.TonsPerHour
										? ` | TPH: ${option.TonsPerHour}${option.TargetTPH ? ` | Base Target: ${option.TargetTPH ?? ""}` : ""}${option.TargetTPHPercentage ? ` | Target %: ${option.TargetTPHPercentage}%` : ""}`
										: ``
								}`}
							</Box>
						)}
					</Box>
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					aria-readonly={readOnly}
					required={required}
					label={label}
					variant="outlined"
					size="small"
					margin={margin}
					fullWidth
					error={!!fieldError}
					helperText={fieldError?.message}
					sx={{
						backgroundColor: (theme) => theme.palette.background.paper,
					}}
					inputProps={{
						...params.inputProps,
						"data-test-id": "section-selector",
					}}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color="inherit" size={20} />
								) : (
									<>
										{params.InputProps.endAdornment}
										{value && size === "medium" ? (
											<IconButton
												component={Link}
												to={`/configuration/sections/${value}`}
												target="_blank"
												size="small"
												title="See in sections"
											>
												<VisibilityIcon sx={{ fontSize: 16 }} />
											</IconButton>
										) : null}
									</>
								)}
							</>
						),
					}}
				/>
			)}
			value={selectedValue}
			onChange={(event, newValue) => {
				onChange(
					newValue?.SectionId ?? null,
					newValue?.SectionName,
					newValue?.TonsPerHour,
					newValue?.TargetTPH ?? null,
					newValue?.TargetTPHPercentage ?? null,
					newValue?.MillYield ?? null,
					newValue?.FinishedYield ?? null,
				);
			}}
		/>
	);

	if (!includeMostUsed) {
		return (
			<>
				<Box>{autocompleteComponent}</Box>
				<ErrorModal
					error={error}
					onHide={() => setError("")}
					title="Error loading sections"
				/>
			</>
		);
	}

	const nextEvents =
		upcomingEvents?.filter((e) => e.EventIndex > 1).slice(0, 10) ?? [];
	return (
		<>
			<Grid2 container spacing={1} alignItems="flex-end">
				<Grid2 size={{ xs: 12, md: 3 }}>{autocompleteComponent}</Grid2>
				<Grid2
					size={{ md: 8 }}
					sx={{
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1.5,
						ml: 1,
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: 0.5,
						}}
					>
						<Typography
							variant="caption"
							color="text.disabled"
							sx={{
								fontSize: "0.65rem",
								lineHeight: 1.5,
								fontWeight: "bold",
							}}
						>
							Current
						</Typography>
						<Chip
							onClick={() => {
								if (onCurrentClick) {
									onCurrentClick();
								} else {
									onChange(
										currentSection?.SectionId ?? null,
										currentSection?.SectionName,
										currentSection?.TonsPerHour,
										currentSection?.TargetTPH,
										currentSection?.TargetTPHPercentage,
									);
								}
							}}
							label={currentSection?.SectionName ?? ""}
							size="medium"
							variant={
								currentSection?.SectionId == selectedValue?.SectionId
									? "filled"
									: "outlined"
							}
							color={
								currentSection?.SectionId == selectedValue?.SectionId
									? "primary"
									: "default"
							}
						/>
					</Box>

					{nextEvents.length > 0 && (
						<Divider
							orientation="vertical"
							flexItem
							sx={{ mx: 0.5, my: 0.5 }}
						/>
					)}

					{nextEvents.length > 0 && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: 0.5,
							}}
						>
							<Typography
								variant="caption"
								color="text.disabled"
								sx={{
									fontSize: "0.65rem",
									lineHeight: 1.5,
									fontWeight: "bold",
								}}
							>
								Next Sections
							</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
								{nextEvents.map((event) => {
									const s = sections?.find(
										(s) => s.SectionId === event.SectionId,
									);
									const isSelected = event.SectionId === value;
									return (
										<Chip
											key={`${event.SectionId}-${event.EventIndex}`}
											label={s?.SectionName}
											size="medium"
											variant={isSelected ? "filled" : "outlined"}
											color={isSelected ? "primary" : "default"}
											onClick={() =>
												onChange(
													s?.SectionId ?? null,
													s?.SectionName,
													s?.TonsPerHour,
													s?.TargetTPH,
													s?.TargetTPHPercentage,
												)
											}
											sx={{ cursor: "pointer" }}
										/>
									);
								})}
							</Box>
						</Box>
					)}
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading sections"
			/>
		</>
	);
};
